import { fetchFirstParty } from "./FirstPartyFetch";
import { reportAppError, reportAppEvent } from "./CoreReport";

export { fetchConfigValues };

// Resilient loading of the collector's config (W2.6, 30/7/2026 finding).
//
// The config fetch was a single point of failure: in flaky webviews (Google
// Ads iOS in-app, "ambos" stratum) every fetch can die for a while right at
// page load. If the config never loads, the collector never initializes — and
// when the visitor submits the form minutes later, with the network alive
// again (their GTM events flow normally), the lead POST is simply never sent.
// Rage-clicks with GTM arriving and no lead: page alive, collector dead.
//
// Three layers of defense, in order:
//   1. First-party route with cross-site fallback (fetchFirstParty).
//   2. Retry with backoff (the network flaps are transient).
//   3. Stale config from localStorage as last resort — the config (endpoints,
//      GTM ids, click-capture defs) changes rarely; a stale copy beats a dead
//      collector every time. Only used when every attempt failed (stale-on-
//      error, not stale-while-revalidate).

const CONFIG_FETCH_ATTEMPTS = 3;
const CONFIG_RETRY_BASE_MS = 1000;

function cacheKeyFor(
  protodealInfoproductId: number,
  protodealBusinessUnitId: number,
): string {
  return `ec_config_${protodealInfoproductId}_${protodealBusinessUnitId}`;
}

async function fetchConfigValues(
  url: string,
  protodealInfoproductId: number,
  protodealBusinessUnitId: number,
): Promise<any> {
  const cacheKey = cacheKeyFor(protodealInfoproductId, protodealBusinessUnitId);
  let lastError: unknown = null;

  for (let attempt = 1; attempt <= CONFIG_FETCH_ATTEMPTS; attempt++) {
    // Reintentos por XHR (misma hipótesis en medición que en el save del lead)
    const transport = attempt > 1 ? "xhr" : "fetch";
    try {
      const resp = await fetchFirstParty(
        url,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ protodealInfoproductId, protodealBusinessUnitId }),
          keepalive: true,
        },
        { transport },
      );
      if (resp.ok) {
        const raw = await resp.json();
        try {
          localStorage.setItem(cacheKey, JSON.stringify(raw));
        } catch (e) {
          // storage blocked/full: cache is best-effort
        }
        if (attempt > 1) {
          // señal fiable: se envía con la red ya recuperada
          reportAppEvent("config_recovered", { app_phase: "config", app_attempt: attempt, app_transport: transport });
        }
        return raw;
      }
      lastError = new Error(`config fetch: ${resp.status} ${resp.statusText}`);
    } catch (e) {
      lastError = e;
    }
    reportAppError(lastError, { app_phase: "config", app_attempt: attempt, app_transport: transport });
    if (attempt < CONFIG_FETCH_ATTEMPTS) {
      await new Promise((resolve) =>
        setTimeout(resolve, attempt * CONFIG_RETRY_BASE_MS),
      );
    }
  }

  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      console.warn(
        "EventCollector: config fetch failed, starting from cached config",
      );
      reportAppEvent("config_stale_cache_used", {
        app_phase: "config",
        app_attempt: CONFIG_FETCH_ATTEMPTS,
      });
      return JSON.parse(cached);
    }
  } catch (e) {
    // storage blocked or corrupt cache: fall through to the real error
  }

  throw lastError;
}
